import { lens, withLenses } from '@dhmk/zustand-lens';
import { merge } from 'lodash';
import { useStore } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { createStore, type StoreApi } from 'zustand/vanilla';

import {
  type BatteryEcommSlice,
  createBatteryEcommSlice,
} from './slices/batteryEcommSlice';
import { createGlobalSlice, type GlobalSlice } from './slices/globalSlice';
import {
  createSunrunShopSlice,
  type SunrunShopSlice,
} from './slices/sunrunShopSlice';

export type AppStoreType = {
  sunrunShop: SunrunShopSlice;
  batteryEcomm: BatteryEcommSlice;
  global: GlobalSlice;
};

// Setup following docs recommendation https://zustand.docs.pmnd.rs/guides/auto-generating-selectors
type WithSelectors<S> = S extends { getState: () => infer T }
  ? S & { use: { [K in keyof T]: () => T[K] } }
  : never;

const createSelectors = <S extends StoreApi<object>>(_store: S) => {
  const store = _store as WithSelectors<typeof _store>;
  store.use = {};
  for (const k of Object.keys(store.getState())) {
    // biome-ignore lint/suspicious/noExplicitAny: Automated migration to Biome
    (store.use as any)[k] = () =>
      useStore(_store, (s) => s[k as keyof typeof s]);
  }
  return store;
};

// Bounded store with named slices to be used for context
// Slices: https://zustand.docs.pmnd.rs/guides/slices-pattern
// Namespacing: https://github.com/dhmk083/dhmk-zustand-lens
// Best Practices from author: https://tkdodo.eu/blog/working-with-zustand
export const createAppBoundStore = () =>
  createSelectors(
    createStore<AppStoreType>()(
      devtools(
        persist(
          withLenses({
            sunrunShop: lens(createSunrunShopSlice),
            batteryEcomm: lens(createBatteryEcommSlice),
            global: lens(createGlobalSlice),
          }),
          {
            name: 'app-store',
            merge: (persistedState, currentState) => {
              return merge({}, currentState, persistedState);
            },
            onRehydrateStorage: () => (state) => {
              state?.global.actions.setHasHydrated(true);
            },
          }
        )
      )
    )
  );
